import { useState, useEffect } from 'react';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useMutation } from '@tanstack/react-query';
import { signupApi, authApi, termsApi } from '@/services/api';
import { toast } from 'react-hot-toast';
import { debug } from '@/utils/debug';
import { useAuthStore } from '@/store/authStore';
import { useUIStore } from '@/store/uiStore';
import { Logo } from '@/components/Logo';
import { DynamicBanner } from '@/components/ui';
import { FreeLifetimePromoModal } from '@/components/FreeLifetimePromoModal';
import { EmailVerificationPendingModal } from '@/components/EmailVerificationPendingModal';
import { EmailVerificationFailedModal } from '@/components/EmailVerificationFailedModal';
import { GoogleSignInButton } from '@/components/GoogleSignInButton';
import { CompanyInfoFields } from '@/components/CompanyInfoFields';
import { TierSelector } from '@/components/features/TierSelector';
import type { SignupFormData, PromoCodeValidationResponse, SignupResponse } from '@/types/signup';
import type { GoogleSignInResponse, GoogleSignInPendingResponse, GoogleCompanyInfo } from '@/types/auth';

// Validation schema for regular signup
const signupSchema = z.object({
  email: z.string().email('Invalid email address'),
  password: z
    .string()
    .min(8, 'Password must be at least 8 characters')
    .regex(/[A-Z]/, 'Password must contain at least one uppercase letter')
    .regex(/[a-z]/, 'Password must contain at least one lowercase letter')
    .regex(/\d/, 'Password must contain at least one digit'),
  confirm_password: z.string(),
  first_name: z.string().min(1, 'First name is required'),
  last_name: z.string().min(1, 'Last name is required'),
  company_info: z.object({
    name: z.string().min(1, 'Company name is required'),
    industry: z.string().optional(),
    address_line1: z.string().min(1, 'Address is required'),
    address_line2: z.string().optional(),
    city: z.string().min(1, 'City is required'),
    state: z.string().min(1, 'State is required'),
    postal_code: z.string().min(1, 'Postal code is required'),
    country: z.string().min(1, 'Country is required'),
    phone: z.string().optional(),
    website: z.union([z.string().url(), z.literal('')]).optional(),
  }),
  promo_code: z.string().optional(),
  tier_type: z.enum(['standard', 'seasonal']).optional(),
  terms_accepted: z.boolean().refine((val) => val === true, {
    message: 'You must agree to the Terms & Conditions and Privacy Policy',
  }),
}).refine((data) => data.password === data.confirm_password, {
  message: 'Passwords do not match',
  path: ['confirm_password'],
});

// Validation schema for Google signup (company info only)
const googleCompanyInfoSchema = z.object({
  name: z.string().min(1, 'Company name is required'),
  industry: z.string().optional(),
  address_line1: z.string().min(1, 'Address is required'),
  address_line2: z.string().optional(),
  city: z.string().min(1, 'City is required'),
  state: z.string().min(1, 'State is required'),
  postal_code: z.string().min(1, 'Postal code is required'),
  country: z.string().min(1, 'Country is required'),
  phone: z.string().optional(),
  website: z.union([z.string().url(), z.literal('')]).optional(),
});

type GoogleCompanyInfoForm = z.infer<typeof googleCompanyInfoSchema>;

export function Signup() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const setAuthFromGoogle = useAuthStore((state) => state.setAuthFromGoogle);
  const [promoValidation, setPromoValidation] = useState<PromoCodeValidationResponse | null>(null);
  // Promo code state is plumbed for re-enabling via the commented-out JSX below.
  // Setter is referenced by validatePromo (also currently unused but retained for re-enable);
  // value is only read by the commented-out JSX, so we destructure it away while parked.
  const [, setIsValidatingPromo] = useState(false);
  // IMPORTANT: This frontend variable must be kept in sync with the backend variable
  // OFFER_LIFETIME_PROMO_CODE (which controls backend code validation)
  // Frontend: VITE_OFFER_LIFETIME_PROMO (controls whether offer is shown in UI)
  // Backend: OFFER_LIFETIME_PROMO_CODE (controls whether code is valid)
  // Promotion disabled - set to false to hide the Free Lifetime Access modal
  const offerEnabled = false;
  const [showPromoModal, setShowPromoModal] = useState(offerEnabled);

  // Google Sign-In state
  const [googlePendingData, setGooglePendingData] = useState<GoogleSignInPendingResponse | null>(null);
  const [googleError, setGoogleError] = useState<string | null>(null);

  // Shared promo code state for both signup flows (see promo plumbing note above).
  // Setter is unused while parked; value is referenced by validatePromo via currentPromoCode.
  const [sharedPromoCode] = useState<string>('');

  // Email verification modal state
  const [showVerificationModal, setShowVerificationModal] = useState(false);
  const [showVerificationFailedModal, setShowVerificationFailedModal] = useState(false);
  const [signupResponse, setSignupResponse] = useState<SignupResponse | null>(null);

  // Tier selection state
  const [selectedTier, setSelectedTier] = useState<'standard' | 'seasonal'>('standard');
  const [googleSelectedTier, setGoogleSelectedTier] = useState<'standard' | 'seasonal'>('standard');

  // Terms acceptance state for Google flow
  const [googleTermsAccepted, setGoogleTermsAccepted] = useState(false);

  // Check for Google pending signup from login page redirect
  useEffect(() => {
    if (searchParams.get('google') === 'pending') {
      const pendingJson = sessionStorage.getItem('googleSignupPending');
      if (pendingJson) {
        try {
          const pending = JSON.parse(pendingJson) as GoogleSignInPendingResponse;
          setGooglePendingData(pending);
          sessionStorage.removeItem('googleSignupPending');
          debug.log('Signup: Restored Google pending signup data', pending);
        } catch (e) {
          debug.error('Signup: Failed to parse Google pending data', e);
        }
      }
    }
  }, [searchParams]);


  // Regular signup form setup
  const {
    register: registerSignup,
    handleSubmit: handleSubmitSignup,
    watch: watchSignup,
    setError: setSignupError,
    setValue: setSignupValue,
    formState: { errors: signupErrors, isSubmitting: isSignupSubmitting },
  } = useForm({
    resolver: zodResolver(signupSchema),
    defaultValues: {
      company_info: {
        website: 'https://',
        country: 'US',
      },
      tier_type: 'standard' as const,
    },
  });

  // Google company info form setup (separate form for Google flow)
  const {
    register: registerGoogle,
    handleSubmit: handleSubmitGoogle,
    watch: watchGoogle,
    formState: { errors: googleErrors, isSubmitting: isGoogleSubmitting },
  } = useForm<GoogleCompanyInfoForm>({
    resolver: zodResolver(googleCompanyInfoSchema),
    defaultValues: {
      website: 'https://',
      country: 'US',
    },
  });

  const promoCode = watchSignup('promo_code');
  const selectedCountrySignup = watchSignup('company_info.country');
  const selectedCountryGoogle = watchGoogle('country');

  // Signup mutation
  const signupMutation = useMutation({
    mutationFn: signupApi.signup,
    onSuccess: async (response) => {
      debug.log('Signup successful:', response);

      // Store auth tokens and set authentication state
      if (response.access_token && response.member_uuid) {
        useAuthStore.getState().setAuthFromSignup(
          response.access_token,
          response.member_uuid,
          response.email,
        );

        debug.log('Set authentication state after signup');

        // Record terms acceptance in the database
        try {
          await termsApi.acceptTermsForSignup();
          debug.log('Terms acceptance recorded successfully');
        } catch (termsError) {
          // Log but don't block signup - terms were accepted via checkbox
          debug.error('Failed to record terms acceptance:', termsError);
        }
      }

      // Store onboarding data
      sessionStorage.setItem(
        'onboarding_data',
        JSON.stringify({
          member_uuid: response.member_uuid,
          session_id: response.onboarding_session_id,
          trial_type: response.trial_type,
          payment_required: response.payment_required,
          tier_type: selectedTier,
        })
      );

      // Check if email verification is required
      if (response.email_verification_required && response.email_verification_sent) {
        // Show verification modal
        setSignupResponse(response);
        setShowVerificationModal(true);

        // Show info message
        toast.success('Account created! Please verify your email to continue.', { duration: 5000 });
      } else if (response.email_verification_required && !response.email_verification_sent) {
        // Email verification required but send failed - block progression
        setSignupResponse(response);
        setShowVerificationFailedModal(true);

        // Log the issue for debugging
        debug.error('Email verification required but email send failed', {
          member_uuid: response.member_uuid,
          email: response.email,
        });

        // Modal will handle resend and support options
        // Note: The account was created but cannot be used until email is verified
      } else {
        // No verification required - proceed with old flow
        let message = `Account created successfully.`;
        if (response.promo_code_applied) {
          message += ` Promo code "${response.promo_code_applied}" applied!`;
        }
        toast.success(message, { duration: 5000 });

        // Redirect to onboarding after short delay
        setTimeout(() => {
          navigate('/onboarding');
        }, 2000);
      }
    },
    onError: (error: Error & { response?: { status?: number; data?: { detail?: string; message?: string } } }) => {
      debug.error('Signup failed:', error);

      // Extract detailed error message
      let errorMessage = 'Signup failed. Please try again.';
      if (error.response?.data?.detail) {
        errorMessage = error.response.data.detail;
      } else if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error.message) {
        errorMessage = error.message;
      }

      // Always log error details to console even in prod
      console.error('Signup Error Details:', {
        status: error.response?.status,
        data: error.response?.data,
        message: errorMessage
      });

      // Check for specific error cases
      const isEmailAlreadyRegistered =
        errorMessage.toLowerCase().includes('email already registered') ||
        errorMessage.toLowerCase().includes('email is already registered') ||
        errorMessage.toLowerCase().includes('already exists');

      console.log('Email already registered check:', { errorMessage, isEmailAlreadyRegistered });

      if (isEmailAlreadyRegistered) {
        // Set form error on email field
        setSignupError('email', {
          type: 'server',
          message: 'This email is already registered. Please use a different email or',
        });

        // Show notification with login suggestion
        useUIStore.getState().showNotification({
          type: 'error',
          message: 'This email is already registered.',
          duration: 8000,
        });
      } else {
        useUIStore.getState().showNotification({
          type: 'error',
          message: errorMessage,
          duration: 6000,
        });
      }
    },
  });

  // Get current promo code based on which flow is active
  const currentPromoCode = googlePendingData ? sharedPromoCode : promoCode;

  // Validate promo code (shared across both signup flows; unused while offerEnabled=false).
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const validatePromo = async () => {
    const codeToValidate = currentPromoCode?.trim();
    if (!codeToValidate) return;

    setIsValidatingPromo(true);
    try {
      const result = await signupApi.validatePromoCode({
        promo_code: codeToValidate.toUpperCase(),
      });
      setPromoValidation(result);

      if (result.is_valid) {
        let message = `Valid! ${result.description}`;
        if (result.trial_override) {
          message += ` (${result.trial_override.duration_months} months`;
          if (!result.trial_override.requires_payment) {
            message += ', no payment required';
          }
          message += ')';
        }
        toast.success(message);
      } else {
        toast.error(result.error_message || 'Invalid promo code');
      }
    } catch (error) {
      debug.error('Promo validation failed:', error);
      toast.error('Failed to validate promo code. Please try again.');
      setPromoValidation(null);
    } finally {
      setIsValidatingPromo(false);
    }
  };

  // Clear promo validation when code changes
  useEffect(() => {
    setPromoValidation(null);
  }, [currentPromoCode]);

  const onSubmitSignup = (data: SignupFormData) => {
    // Clean up promo code and include tier type
    const cleanedData = {
      ...data,
      promo_code: promoValidation?.is_valid ? promoValidation.code : undefined,
      tier_type: selectedTier,
      company_info: {
        ...data.company_info,
        industry: data.company_info.industry || null,
        address_line2: data.company_info.address_line2 || null,
        phone: data.company_info.phone || null,
        website: data.company_info.website || null,
      },
    };

    signupMutation.mutate(cleanedData);
  };

  // Handle tier selection change
  const handleTierChange = (tier: 'standard' | 'seasonal') => {
    setSelectedTier(tier);
    setSignupValue('tier_type', tier);
  };

  // Google Sign-In handlers
  const handleGoogleSuccess = (response: GoogleSignInResponse) => {
    debug.log('Signup: Google Sign-In successful (existing user)');
    setAuthFromGoogle(response.access_token, response.member_uuid);
    toast.success('Signed in with Google successfully!');
    navigate('/dashboard');
  };

  const handleGoogleNeedCompanyInfo = (pendingData: GoogleSignInPendingResponse) => {
    debug.log('Signup: Google user needs company info', pendingData);
    setGooglePendingData(pendingData);
    setGoogleError(null);
  };

  const handleGoogleError = (error: string) => {
    debug.error('Signup: Google Sign-In failed', error);
    setGoogleError(error);
  };

  // Google complete signup mutation
  const googleCompleteSignupMutation = useMutation({
    mutationFn: authApi.googleCompleteSignup,
    onSuccess: async (response) => {
      debug.log('Signup: Google signup complete', response);
      await setAuthFromGoogle(response.access_token, response.member_uuid);

      // Record terms acceptance in the database
      try {
        await termsApi.acceptTermsForSignup();
        debug.log('Terms acceptance recorded successfully for Google signup');
      } catch (termsError) {
        // Log but don't block signup - terms were accepted via checkbox
        debug.error('Failed to record terms acceptance for Google signup:', termsError);
      }

      toast.success('Account created successfully!');

      // Store onboarding data (similar to regular signup)
      sessionStorage.setItem(
        'onboarding_data',
        JSON.stringify({
          member_uuid: response.member_uuid,
          session_id: null,
          trial_type: 'standard',
          payment_required: false,
          tier_type: googleSelectedTier,
        })
      );

      navigate('/onboarding');
    },
    onError: (error: Error & { response?: { data?: { detail?: string } } }) => {
      debug.error('Signup: Google signup completion failed', error);
      const errorMessage = error.response?.data?.detail || 'Failed to complete signup';
      toast.error(errorMessage);
    },
  });

  // Handle Google company info form submission
  const onSubmitGoogleCompanyInfo = (data: GoogleCompanyInfoForm) => {
    if (!googlePendingData) return;

    const companyInfo: GoogleCompanyInfo = {
      name: data.name,
      industry: data.industry || undefined,
      address_line1: data.address_line1,
      address_line2: data.address_line2 || undefined,
      city: data.city,
      state: data.state,
      postal_code: data.postal_code,
      country: data.country,
      phone: data.phone || undefined,
      website: data.website || undefined,
    };

    googleCompleteSignupMutation.mutate({
      temp_token: googlePendingData.temp_token,
      company_info: companyInfo,
      promo_code: promoValidation?.is_valid ? promoValidation.code : undefined,
      tier_type: googleSelectedTier,
    });
  };

  // Cancel Google signup and go back to regular form
  const handleCancelGoogleSignup = () => {
    setGooglePendingData(null);
    setGoogleError(null);
  };

  return (
    <div className="min-h-screen bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-3xl mx-auto">
        {/* Logo */}
        <div className="text-center mb-8">
          <div className="flex justify-center mb-4">
            <Logo variant="color" size="lg" className="!h-[64px] ml-6" />
          </div>
          <p className="text-gray-600">Let's get to flourishing.</p>
        </div>

        {/* Free Tier Banner */}
        <DynamicBanner className="mb-8">
          Free access - You only pay Google for your ads, not us!
        </DynamicBanner>

        {/* Signup Form */}
        <div className="bg-white rounded-lg shadow-md p-8">
          <h2 className="text-2xl font-bold text-gray-900 mb-6">
            {googlePendingData ? 'Complete Your Account' : 'Create Your Account'}
          </h2>

          {/* Google Sign-In Button (only show if not in Google pending mode) */}
          {!googlePendingData && (
            <>
              <div className="flex justify-center mb-4">
                <GoogleSignInButton
                  mode="signup"
                  onSuccess={handleGoogleSuccess}
                  onNeedCompanyInfo={handleGoogleNeedCompanyInfo}
                  onError={handleGoogleError}
                  disabled={isSignupSubmitting || signupMutation.isPending}
                  width={320}
                />
              </div>

              {/* Google Error Message */}
              {googleError && (
                <div className="p-3 mb-4 bg-red-50 border border-red-200 rounded-md">
                  <p className="text-sm text-red-600">{googleError}</p>
                </div>
              )}

              {/* Divider */}
              <div className="relative mb-6">
                <div className="absolute inset-0 flex items-center">
                  <div className="w-full border-t border-gray-300" />
                </div>
                <div className="relative flex justify-center text-sm">
                  <span className="px-2 bg-white text-gray-500">or sign up with email</span>
                </div>
              </div>
            </>
          )}

          {/* Google Pending Mode - Show user info and company form only */}
          {googlePendingData ? (
            <>
              <div className="mb-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-blue-800">
                      Signing up as <strong>{googlePendingData.email}</strong>
                    </p>
                    {(googlePendingData.first_name || googlePendingData.last_name) && (
                      <p className="text-sm text-blue-600">
                        {googlePendingData.first_name} {googlePendingData.last_name}
                      </p>
                    )}
                  </div>
                  <button
                    type="button"
                    onClick={handleCancelGoogleSignup}
                    className="text-sm text-blue-600 hover:text-blue-800 underline"
                  >
                    Use different account
                  </button>
                </div>
              </div>

              {/* Google Company Info Form */}
              <form onSubmit={handleSubmitGoogle(onSubmitGoogleCompanyInfo)} className="space-y-6">
                <CompanyInfoFields
                  register={registerGoogle}
                  errors={googleErrors}
                  selectedCountry={selectedCountryGoogle || 'US'}
                  showTitle={true}
                />

                {/* Tier Selection for Google signup */}
                <div className="pt-4">
                  <TierSelector
                    selectedTier={googleSelectedTier}
                    onTierChange={setGoogleSelectedTier}
                    mode="select"
                  />
                </div>

                {/* Promo Code for Google signup - Hidden for now */}
                {/* TODO: Uncomment when promo codes are ready
                <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                  <h3 className="text-sm font-semibold text-gray-900 mb-3">Have a promo code?</h3>
                  <div className="flex gap-2">
                    <input
                      type="text"
                      value={sharedPromoCode}
                      onChange={(e) => setSharedPromoCode(e.target.value)}
                      placeholder="Enter code here"
                      className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 uppercase"
                    />
                    <button
                      type="button"
                      onClick={validatePromo}
                      disabled={!sharedPromoCode?.trim() || isValidatingPromo}
                      className="px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700 disabled:bg-gray-400 disabled:cursor-not-allowed"
                    >
                      {isValidatingPromo ? 'Validating...' : 'Validate'}
                    </button>
                  </div>
                  {promoValidation && (
                    <div
                      className={`mt-2 p-2 rounded-md text-sm font-medium ${
                        promoValidation.is_valid
                          ? 'bg-gray-100 text-flouri-darkGray border border-gray-300'
                          : 'bg-primary-50 text-primary-700 border border-primary-200'
                      }`}
                    >
                      {promoValidation.is_valid
                        ? `${promoValidation.description}`
                        : `${promoValidation.error_message}`}
                    </div>
                  )}
                </div>
                */}

                {/* Terms & Conditions Checkbox */}
                <div className="pt-4">
                  <label className="flex items-start gap-3 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={googleTermsAccepted}
                      onChange={(e) => setGoogleTermsAccepted(e.target.checked)}
                      className="mt-1 h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                    />
                    <span className="text-sm text-gray-700">
                      I agree to the{' '}
                      <Link
                        to="/terms"
                        target="_blank"
                        className="text-blue-600 hover:text-blue-800 underline"
                        onClick={(e) => e.stopPropagation()}
                      >
                        Terms & Conditions
                      </Link>{' '}
                      and{' '}
                      <Link
                        to="/privacy"
                        target="_blank"
                        className="text-blue-600 hover:text-blue-800 underline"
                        onClick={(e) => e.stopPropagation()}
                      >
                        Privacy Policy
                      </Link>
                    </span>
                  </label>
                </div>

                {/* Submit Button */}
                <div className="flex items-center justify-between pt-6">
                  <button
                    type="button"
                    onClick={handleCancelGoogleSignup}
                    className="text-sm text-gray-600 hover:text-gray-800"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={isGoogleSubmitting || googleCompleteSignupMutation.isPending || !googleTermsAccepted}
                    className="px-6 py-3 bg-blue-800 text-white rounded-md hover:bg-blue-900 disabled:bg-gray-400 disabled:cursor-not-allowed font-medium"
                  >
                    {isGoogleSubmitting || googleCompleteSignupMutation.isPending ? 'Creating Account...' : 'Create Account'}
                  </button>
                </div>
              </form>
            </>
          ) : (
            /* Regular Signup Form */
            <form onSubmit={handleSubmitSignup(onSubmitSignup)} className="space-y-6">
              {/* Personal Information */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    First Name *
                  </label>
                  <input
                    {...registerSignup('first_name')}
                    type="text"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                  {signupErrors.first_name && (
                    <p className="mt-1 text-sm text-red-600">{signupErrors.first_name.message}</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Last Name *
                  </label>
                  <input
                    {...registerSignup('last_name')}
                    type="text"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                  {signupErrors.last_name && (
                    <p className="mt-1 text-sm text-red-600">{signupErrors.last_name.message}</p>
                  )}
                </div>
              </div>

              {/* Email */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Email Address *
                </label>
                <input
                  {...registerSignup('email')}
                  type="email"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
                {signupErrors.email && (
                  <div className="mt-1 text-sm text-red-600">
                    {signupErrors.email.message}
                    {signupErrors.email.type === 'server' && (
                      <>
                        {' '}
                        <Link
                          to="/login"
                          className="text-blue-600 underline hover:text-blue-800 font-medium"
                        >
                          login to your existing account
                        </Link>
                        .
                      </>
                    )}
                  </div>
                )}
              </div>

              {/* Password */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Password *
                  </label>
                  <input
                    {...registerSignup('password')}
                    type="password"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                  {signupErrors.password && (
                    <p className="mt-1 text-sm text-red-600">{signupErrors.password.message}</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Confirm Password *
                  </label>
                  <input
                    {...registerSignup('confirm_password')}
                    type="password"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                  {signupErrors.confirm_password && (
                    <p className="mt-1 text-sm text-red-600">{signupErrors.confirm_password.message}</p>
                  )}
                </div>
              </div>

              {/* Company Information - Using shared component */}
              <CompanyInfoFields
                register={registerSignup}
                errors={signupErrors}
                selectedCountry={selectedCountrySignup || 'US'}
                fieldPrefix="company_info"
                showTitle={true}
              />

              {/* Tier Selection */}
              <div className="pt-4">
                <TierSelector
                  selectedTier={selectedTier}
                  onTierChange={handleTierChange}
                  mode="select"
                />
              </div>

              {/* Promo Code - Hidden for now */}
              {/* TODO: Uncomment when promo codes are ready
              <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                <h3 className="text-sm font-semibold text-gray-900 mb-3">Have a promo code?</h3>
                <div className="flex gap-2">
                  <input
                    {...registerSignup('promo_code')}
                    type="text"
                    placeholder="Enter code here"
                    className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 uppercase"
                  />
                  <button
                    type="button"
                    onClick={validatePromo}
                    disabled={!currentPromoCode?.trim() || isValidatingPromo}
                    className="px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700 disabled:bg-gray-400 disabled:cursor-not-allowed"
                  >
                    {isValidatingPromo ? 'Validating...' : 'Validate'}
                  </button>
                </div>
                {promoValidation && (
                  <div
                    className={`mt-2 p-2 rounded-md text-sm font-medium ${
                      promoValidation.is_valid
                        ? 'bg-gray-100 text-flouri-darkGray border border-gray-300'
                        : 'bg-primary-50 text-primary-700 border border-primary-200'
                    }`}
                  >
                    {promoValidation.is_valid
                      ? `${promoValidation.description}`
                      : `${promoValidation.error_message}`}
                  </div>
                )}
              </div>
              */}

              {/* Terms & Conditions Checkbox */}
              <div className="pt-4">
                <label className="flex items-start gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    {...registerSignup('terms_accepted')}
                    className="mt-1 h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                  />
                  <span className="text-sm text-gray-700">
                    I agree to the{' '}
                    <Link
                      to="/terms"
                      target="_blank"
                      className="text-blue-600 hover:text-blue-800 underline"
                      onClick={(e) => e.stopPropagation()}
                    >
                      Terms & Conditions
                    </Link>{' '}
                    and{' '}
                    <Link
                      to="/privacy"
                      target="_blank"
                      className="text-blue-600 hover:text-blue-800 underline"
                      onClick={(e) => e.stopPropagation()}
                    >
                      Privacy Policy
                    </Link>
                  </span>
                </label>
                {signupErrors.terms_accepted && (
                  <p className="mt-1 ml-7 text-sm text-red-600">{signupErrors.terms_accepted.message}</p>
                )}
              </div>

              {/* Submit Button */}
              <div className="flex items-center justify-between pt-6">
                <Link to="/login" className="text-sm text-blue-600 hover:text-blue-800">
                  Already have an account? Sign in
                </Link>
                <button
                  type="submit"
                  disabled={isSignupSubmitting || signupMutation.isPending}
                  className="px-6 py-3 bg-blue-800 text-white rounded-md hover:bg-blue-900 disabled:bg-gray-400 disabled:cursor-not-allowed font-medium"
                >
                  {isSignupSubmitting || signupMutation.isPending ? 'Creating Account...' : 'Create Account'}
                </button>
              </div>
            </form>
          )}
        </div>
      </div>

      {/* Free Lifetime Promo Modal */}
      <FreeLifetimePromoModal
        isOpen={showPromoModal}
        onClose={() => setShowPromoModal(false)}
        showInstructions={true}
      />

      {/* Email Verification Pending Modal */}
      {signupResponse && (
        <EmailVerificationPendingModal
          isOpen={showVerificationModal}
          onVerified={() => {
            // Fire the Google Ads signup conversion HERE — this is the
            // load-bearing "signup just completed" moment, stable and
            // race-free. The previous home for this fire (Onboarding's
            // useEffect on `?verified=true`) was unreliable because
            // /onboarding can redirect to /signup on missing session data,
            // and the gtag fire would race the redirect. By the time we're
            // in this callback, the signup tab is alive with gtag fully
            // loaded and no impending navigation.
            const sendTo = import.meta.env.VITE_SIGNUP_CONVERSION_SEND_TO;
            if (
              sendTo &&
              typeof window.gtag === 'function' &&
              sessionStorage.getItem('signup_conversion_fired') !== '1'
            ) {
              window.gtag('event', 'conversion', { send_to: sendTo });
              sessionStorage.setItem('signup_conversion_fired', '1');
              debug.log('Signup: Google Ads signup conversion fired');
            }

            // Mirror — Meta Pixel CompleteRegistration. Same trigger
            // moment (verified email = signup complete), same
            // once-per-session guard. value:0 because Flouri free tier
            // is non-paid; currency kept for schema consistency.
            //
            // eventID is a random per-session UUID (not derived from
            // member_uuid) so we don't leak Flouri's stable internal
            // user identifier to Meta. Stored in sessionStorage so the
            // Onboarding fallback path (and any future CAPI server-side
            // fire of the same event) can dedupe against the same id.
            if (
              typeof window.fbq === 'function' &&
              sessionStorage.getItem('meta_signup_conversion_fired') !== '1'
            ) {
              let metaEventID = sessionStorage.getItem('meta_signup_event_id');
              if (!metaEventID) {
                metaEventID = crypto.randomUUID();
                sessionStorage.setItem('meta_signup_event_id', metaEventID);
              }
              window.fbq(
                'track',
                'CompleteRegistration',
                { value: 0, currency: 'USD' },
                { eventID: metaEventID },
              );
              sessionStorage.setItem('meta_signup_conversion_fired', '1');
              debug.log('Signup: Meta CompleteRegistration conversion fired');
            }

            setShowVerificationModal(false);
            toast.success('Email verified! Proceeding to account setup...', { duration: 3000 });
            setTimeout(() => {
              navigate('/onboarding?verified=true');
            }, 1000);
          }}
          email={signupResponse.email}
          memberUuid={signupResponse.member_uuid}
        />
      )}

      {/* Email Verification Failed Modal */}
      {signupResponse && (
        <EmailVerificationFailedModal
          isOpen={showVerificationFailedModal}
          onClose={() => setShowVerificationFailedModal(false)}
          onResendSuccess={() => {
            // Transition from failed modal to pending modal
            setShowVerificationFailedModal(false);
            setShowVerificationModal(true);
          }}
          email={signupResponse.email}
          memberUuid={signupResponse.member_uuid}
        />
      )}
    </div>
  );
}
